import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articlesList, articleDetails } from '@/mocks/articles';

interface Category {
  name: string;
  url: string;
}

const relatedCategories: Category[] = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies & Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
];

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? articleDetails[slug] : null;
  const articleMeta = slug ? articlesList.find((a) => a.slug === slug) : null;

  if (!article || !articleMeta) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist yet.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: articleMeta.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: '2026-06-25',
            dateModified: '2026-06-29',
            publisher: {
              '@type': 'Organization',
              name: 'VigorBuy',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg',
              },
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: import.meta.env.VITE_SITE_URL || 'https://vigorbuy.study',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Guides',
                item: (import.meta.env.VITE_SITE_URL || 'https://vigorbuy.study') + '/',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main>
        <article className="pt-20 md:pt-24">
          {/* Breadcrumb */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 mb-8">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-foreground-950 transition-colors cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <Link to="/" className="hover:text-foreground-950 transition-colors cursor-pointer">
                Guides
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>

          {/* Article Header */}
          <header className="max-w-3xl mx-auto px-4 md:px-6 mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">Updated June 2026</span>
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              {articleMeta.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-background-200/70">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-accent-200 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20woman%20in%20her%20thirties%20with%20warm%20confident%20smile%20wearing%20cream%20blouse%20against%20soft%20beige%20studio%20backdrop%20natural%20window%20light%20editorial%20quality%20clean%20minimal%20composition&width=80&height=80&seq=author-avatar-article-v2-j3k7m&orientation=squarish"
                  alt={article.author}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-950">{article.author}</p>
                <p className="text-xs text-foreground-500">Editor &amp; Product Reviewer &middot; {article.readTime}</p>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 mb-12">
            <div className="rounded-xl overflow-hidden h-64 md:h-96">
              <img
                src={article.heroImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Article Body */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 pb-12">
            <div className="space-y-10 text-foreground-700 leading-relaxed">
              {article.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.content.map((para, pidx) => (
                    <p key={pidx} className="text-sm md:text-base mb-4 last:mb-0">
                      {para.split(/(Vigorbuy Spreadsheet|Vigorbuy|Kaspreadsheet|homepage|Resource Center)/g).map((part, i) => {
                        if (part === 'Vigorbuy Spreadsheet') {
                          return <strong key={i}>{part}</strong>;
                        }
                        if (part === 'Vigorbuy') {
                          return <strong key={i}>{part}</strong>;
                        }
                        if (part === 'Kaspreadsheet') {
                          return <a key={i} href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">{part}</a>;
                        }
                        if (part === 'homepage' || part === 'Resource Center') {
                          return <Link key={i} to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">{part}</Link>;
                        }
                        return part;
                      })}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            {/* Internal CTA */}
            <div className="mt-12 p-6 rounded-lg bg-accent-100/50 border border-accent-200/60">
              <p className="text-sm md:text-base font-semibold text-foreground-950 mb-2">
                Ready to start exploring?
              </p>
              <p className="text-sm text-foreground-600 mb-4">
                Visit the <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer font-medium">Vigorbuy Resource Center</Link> to browse all categories, or jump directly to the spreadsheet on Kaspreadsheet.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                Explore Vigorbuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            {/* Related Categories */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                Explore Related Categories
              </h3>
              <div className="flex flex-wrap gap-2">
                {relatedCategories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-200/50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            {/* More Articles */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                Continue Reading
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {articlesList
                  .filter((a) => a.slug !== slug)
                  .slice(0, 3)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/articles/${related.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer"
                    >
                      <div className="h-32 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-1">{related.readTime}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-8 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-950 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Vigorbuy Home
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}